<!-- v8 ignore file -->
<script setup lang="ts">
/* v8 ignore file */
/**
 * Модальное окно продукта: одиночный товар и группа вариантов (radio).
 * Комбо рендерится отдельным конструктором ComboBuilder — у него своя вёрстка
 * (слоты, «Заменить», «Изменить состав»), общего с карточкой товара почти нет.
 *
 * SRP: весь тяжёлый стейт вынесен в composables:
 *   - useProductVariants       → выбор варианта
 *   - useProductCustomization  → addons / excludes / totalPrice
 *   - useCartProduct           → quantity / inCart / addToCart / removeFromCart
 *   - useProductBundles        → bundles / load / openBundleBuilder
 */

import { ref, computed, toRef, onMounted, watch, unref, nextTick } from 'vue';
import { consola } from 'consola';

// ─── Stores ─────────────────────────────────────────────────────────────────
import { useProductModalAnalytics } from '../model/useProductModalAnalytics';
import { useCartStore } from '@entities/order';
import { useInstitutionStore } from '@entities/institution';
import { useModalStore } from '@shared/model';
import { useAuthStore } from '~/features/auth';

// ─── Types, guards & composables ─────────────────────────────────────────────
import {
  type Product,
  type ComboProduct,
  isProductGroup,
  isComboProduct,
  useProductVariants,
  useProductCustomization,
} from '~/entities/product';
import { useCartProduct } from '@features/basket';
import { useProductBundles } from '~/features/bundle';

// ─── UI components ───────────────────────────────────────────────────────────
import ProductMediaSlider from '~/entities/product/ui/ProductMediaSlider.vue';
import ProductVariantSelector from '~/entities/product/ui/ProductVariantSelector.vue';
import ProductInfo from '~/entities/product/ui/ProductInfo.vue';
import ProductAddons from '~/entities/product/ui/ProductAddons.vue';
import ProductExcludes from '~/entities/product/ui/ProductExcludes.vue';
import ProductSettingsSheet from '~/entities/product/ui/ProductSettingsSheet.vue';
import ProductInfoModal from '~/entities/product/ui/ProductInfoModal.vue';
import ProductActions from '~/entities/product/ui/ProductActions.vue';
import ComboBuilder from '~/features/combo/ui/builder/ComboBuilder.vue';
import ProductBundlesSection from '~/features/bundle/ui/ProductBundlesSection.vue';
import BundleBuilderOverlay from '~/features/bundle/ui/BundleBuilderOverlay.vue';

// ─── Shared ──────────────────────────────────────────────────────────────────
import { isMdViewport } from '~/shared/lib';

// ─── Аналитика ───────────────────────────────────────────────────────────────

// ─── Emits ───────────────────────────────────────────────────────────────────
const emit = defineEmits(['update:size']);

// ─── Constants ───────────────────────────────────────────────────────────────
const SHOW_LIMIT = 8;
const logger = consola.withTag('MoreAboutDish');

// ─── Stores ─────────────────────────────────────────────────────────────────
const storeModal = useModalStore();
const cartStore = useCartStore();
const institutionStore = useInstitutionStore();
const storeAuth = useAuthStore();

// ─── Базовые вычисляемые данные ──────────────────────────────────────────────
const placeholderImg = computed(() => (institutionStore.institution as any)?.visual?.placeholder_file);
const selectedProduct = computed(() => cartStore.selectedDish as Product | null);
const preferredVariantId = toRef(cartStore, 'preferredVariantId');
const isCombo = computed(() => !!selectedProduct.value && isComboProduct(selectedProduct.value));

const branchId = computed(() =>
  institutionStore.selectedBranch?.id ? String(institutionStore.selectedBranch.id) : null,
);

// Типизированный геттер для ComboProduct, чтобы не кастовать в шаблоне
const comboProduct = computed<ComboProduct | null>(() =>
  isCombo.value ? (selectedProduct.value as ComboProduct) : null,
);

// ─── Варианты продукта ───────────────────────────────────────────────────────
const { selectedVariantIndex, currentVariant, hasVariants, useRadioButtons } = useProductVariants({
  product: selectedProduct,
  preferredVariantId: preferredVariantId as any,
});

const variants = computed(() => {
  if (!isProductGroup(selectedProduct.value)) return [];
  return selectedProduct.value.variants;
});

// ─── Кастомизация (addons / excludes) ────────────────────────────────────────
const {
  selectedAddons,
  selectedExcludes,
  showAllAddons,
  showAllExcludes,
  visibleAddons,
  visibleExcludes,
  hasMoreAddons,
  hasMoreExcludes,
  addonsSum,
  totalPrice,
  hasCustomization,
  getSortedIds,
} = useProductCustomization({ currentVariant, showLimit: SHOW_LIMIT });

// ─── Аналитика воронки карточки ──────────────────────────────────────────────
// Открытие, выбор варианта, переключение модификаторов и закрытие composable ловит сам;
// компонент сообщает только про исход добавления в корзину.
const { trackAdded, trackFailed } = useProductModalAnalytics({
  product: selectedProduct,
  currentVariant,
  selectedAddons,
  selectedExcludes,
  useRadioButtons,
});

// ─── Корзина обычного товара ─────────────────────────────────────────────────
const addonsArr = computed(() => getSortedIds(selectedAddons.value) as any);
const excludesArr = computed(() => getSortedIds(selectedExcludes.value) as any);

const productObj = computed(() => {
  const imgs = isProductGroup(selectedProduct.value) ? selectedProduct.value.img ?? [] : currentVariant.value.img ?? [];

  return {
    id: String(currentVariant.value.id),
    name: currentVariant.value.name,
    price: totalPrice.value,
    img: [...imgs],
  };
});

const {
  quantity,
  inCart,
  addToCart: addToCartBase,
  cartItem,
  removeFromCart,
} = useCartProduct({
  product: productObj,
  branchId,
  addons: addonsArr as any,
  excludes: excludesArr as any,
});

// ─── Бандлы («Собрать набор») ─────────────────────────────────────────────────
const bundleProductId = computed(() => currentVariant.value?.id ?? (selectedProduct.value as any)?.id);

const {
  bundles,
  hasBundles,
  showBundleBuilder,
  selectedBundle,
  openBundleBuilder,
  load: loadBundles,
} = useProductBundles({ productId: bundleProductId, branchId, isCombo });

const baseProductImgForBundle = computed<string | null>(() => {
  const img = (currentVariant.value.img as any[])?.[0];
  if (!img) return null;
  return typeof img === 'string' ? img : img.url ?? null;
});

// ─── UI State ────────────────────────────────────────────────────────────────
const showSettings = ref(false);
const showProductInfo = ref(false);
const isAddingToCart = ref(false);

/**
 * После первого показа модификаторов (scroll или bottom sheet)
 * повторный клик на кнопку цены добавляет в корзину.
 */
const priceButtonModifiersRevealed = ref(false);

const isLoading = computed(() => isAddingToCart.value);

// ─── Состояние карточки товара ───────────────────────────────────────────────
// Комбо рендерится отдельным конструктором (ComboBuilder) — здесь только товары.
const productState = computed(() => ({
  inCart: inCart.value,
  quantity: quantity.value,
  name: currentVariant.value.name,
  totalPrice: totalPrice.value,
  tags: (currentVariant.value as any)?.tags,
  teg: (currentVariant.value as any)?.teg,
}));

const quantityStep = computed(() => currentVariant.value.quantity_step ?? 1);
const minQuantity = computed(() => currentVariant.value.min_quantity ?? 0);

const needsPriceButtonTwoStep = computed(() => hasCustomization.value || hasVariants.value);

const stopListMessage = computed(() => (currentVariant.value.stop_list ? 'Товар временно недоступен для заказа' : ''));

const isDisabled = computed(() => !!currentVariant.value.stop_list);

const actionDisabledMessage = computed(() => stopListMessage.value);

const hasProductInfo = computed(
  () => !!(currentVariant.value.kbju?.proteins || currentVariant.value.description || currentVariant.value.weight),
);

const productBackgroundImg = computed(() => {
  const variantBg = (currentVariant.value as any)?.background_img;
  const selectedBg = (selectedProduct.value as any)?.background_img;
  return variantBg ?? selectedBg ?? null;
});

/** URL фона грида — только `background_img`, не фото блюда. */
const gridBackgroundUrl = computed(() => productBackgroundImg.value?.url ?? null);

// ─── Refs для скролла ─────────────────────────────────────────────────────────
const desktopSettingsEl = ref<HTMLElement | null>(null);
const variantSectionEl = ref<HTMLElement | null>(null);

// ─── Действия с корзиной ─────────────────────────────────────────────────────
const addToCart = async () => {
  isAddingToCart.value = true;
  try {
    await addToCartBase({ quantity: minQuantity.value });
    await nextTick();
    // Корректируем, если товар добавился с количеством 1, но minQuantity > 1
    if (
      cartItem.value &&
      cartItem.value.quantity === 1 &&
      minQuantity.value > 0 &&
      Math.abs(minQuantity.value - 1) > 0.01
    ) {
      await cartStore.updateQuantity(
        cartItem.value.id,
        minQuantity.value,
        Number(branchId.value),
        unref(addonsArr),
        unref(excludesArr),
      );
    }

    // Считаем добавление состоявшимся только здесь: до этой строки позиция могла
    // не пройти проверку доступности на филиале или не долететь до сервера.
    trackAdded({
      quantity: cartItem.value?.quantity ?? Math.max(minQuantity.value, 1),
      total: totalPrice.value * (cartItem.value?.quantity ?? Math.max(minQuantity.value, 1)),
    });
  } catch (error) {
    logger.error('Error adding to cart:', error);
    trackFailed('unknown', error instanceof Error ? error.message : String(error));
  } finally {
    isAddingToCart.value = false;
  }
};

const handleQuantityChange = async (newQuantity: number) => {
  if (!branchId.value || !cartItem.value) return;
  if (newQuantity === quantity.value) return;

  isAddingToCart.value = true;
  try {
    if (newQuantity <= 0) {
      await removeFromCart();
    } else {
      await cartStore.updateQuantity(
        cartItem.value.id,
        newQuantity,
        Number(branchId.value),
        unref(addonsArr),
        unref(excludesArr),
      );
      await nextTick();
    }
  } catch (error) {
    logger.error('Error changing quantity:', error);
    if (branchId.value && storeAuth.isLoggedIn) {
      await cartStore.loadCartFromServer(Number(branchId.value));
    }
  } finally {
    isAddingToCart.value = false;
  }
};

const onQuantityChange = (newQuantity: number) => handleQuantityChange(newQuantity);

// ─── UI-оркестрация ───────────────────────────────────────────────────────────
/** Открывает bottom sheet кастомизации товара */
const openMobileCustomizeSheet = () => {
  showSettings.value = true;
};

const openBasket = () => {
  storeModal.close();
  cartStore.openBasket();
};

const revealModifiersForPriceButton = async () => {
  await nextTick();
  if (hasCustomization.value) {
    if (!isMdViewport()) {
      showSettings.value = true;
      return;
    }
    desktopSettingsEl.value?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    priceButtonModifiersRevealed.value = true;
    return;
  }
  if (hasVariants.value) {
    variantSectionEl.value?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    priceButtonModifiersRevealed.value = true;
  }
};

const handlePrimaryPriceClick = async () => {
  if (isDisabled.value || isLoading.value) return;

  // На desktop модификаторы/варианты уже видны → добавляем сразу
  if (isMdViewport()) {
    await addToCart();
    return;
  }

  if (!needsPriceButtonTwoStep.value || priceButtonModifiersRevealed.value) {
    await addToCart();
    return;
  }

  await revealModifiersForPriceButton();
};

// ─── Lifecycle & Watchers ────────────────────────────────────────────────────
// Уведомляем модальный контейнер о нужном размере при создании и повторном открытии
emit('update:size', 'xl');
onMounted(() => {
  emit('update:size', 'xl');
  loadBundles();
});

watch(
  () => currentVariant.value?.id,
  (newId, oldId) => {
    priceButtonModifiersRevealed.value = false;
    if (newId && newId !== oldId) loadBundles();
  },
);

watch(
  () => (selectedProduct.value as Product | ComboProduct | null)?.id,
  () => {
    priceButtonModifiersRevealed.value = false;
  },
);

watch(showSettings, (opened) => {
  if (opened) priceButtonModifiersRevealed.value = true;
});
</script>

<template>
  <div class="more-about-dish" data-testid="product-modal">
    <!-- Комбо: собственный конструктор (слоты + «Заменить» + «Изменить состав») -->
    <ComboBuilder
      v-if="isCombo"
      :combo="comboProduct"
      :branch-id="branchId"
      @open-basket="openBasket"
    />

    <div
      v-else
      class="more-about-dish__grid"
      :style="gridBackgroundUrl ? { backgroundImage: `url(${gridBackgroundUrl})` } : {}"
    >
      <!-- Медиа слайдер -->
      <ProductMediaSlider
        :current-variant="currentVariant"
        :placeholder-img="placeholderImg"
        :on-background="!!gridBackgroundUrl"
      />

      <!-- Кнопка «Изменить состав» (мобильный) -->
      <div v-if="hasCustomization" class="more-about-dish__customize-button">
        <UiButtonMain
          icon="i-lucide-pen-line"
          color="orange"
          size="md"
          class="shake-animation"
          label="Изменить состав"
          @click="openMobileCustomizeSheet"
        />
      </div>

      <!-- Контент (плавное появление снизу при открытии модалки) -->
      <Transition name="more-about-dish-content" appear>
        <div class="more-about-dish__content">
          <div class="more-about-dish__content-scroll">
          <!-- Заголовок -->
          <div class="more-about-dish__title">{{ productState.name }}</div>

          <!-- Badges и кнопка «Состав» -->
          <div class="more-about-dish__header-actions">
            <ProductBadges
              :tags="productState.tags"
              :teg="productState.teg"
              size="md"
              :max-visible="4"
            />

            <button v-if="hasProductInfo" class="more-about-dish__info-button" @click="showProductInfo = true">
              <span class="text-base font-medium">Состав</span>
              <UIcon name="i-heroicons-information-circle" class="w-5 h-5" />
            </button>
          </div>

          <!-- Сообщение о недоступности -->
          <div v-if="stopListMessage" class="more-about-dish__disabled-message">
            {{ stopListMessage }}
          </div>

          <!-- Информация о продукте (Desktop) -->
          <ProductInfo
            class="more-about-dish__info--desktop"
            :description="currentVariant.description"
            :amount="currentVariant.amount"
            :volume="currentVariant.volume"
            :weight="currentVariant.weight"
            :kbju="currentVariant.kbju"
            :composition="currentVariant.composition"
            :allergens="currentVariant.allergens"
            :show-kbju="true"
          />

          <!-- Выбор вариантов -->
          <div v-if="hasVariants" ref="variantSectionEl">
            <ProductVariantSelector
              v-model="selectedVariantIndex"
              :variants="variants"
              :use-radio-buttons="useRadioButtons"
            />
          </div>

          <!-- Настройки (Desktop) -->
          <div ref="desktopSettingsEl" class="more-about-dish__settings--desktop">
            <ProductAddons
              v-if="currentVariant.addons"
              v-model="selectedAddons"
              :addons="visibleAddons"
              :show-limit="SHOW_LIMIT"
            >
              <template #show-more-button>
                <UButton
                  v-if="hasMoreAddons"
                  variant="ghost"
                  color="white"
                  size="sm"
                  icon="heroicons:chevron-down-20-solid"
                  trailing
                  class="mt-2 w-full hover:!bg-transparent"
                  @click="showAllAddons = true"
                >
                  Показать ещё
                </UButton>
              </template>
            </ProductAddons>

            <ProductExcludes
              v-if="currentVariant.excludes"
              v-model="selectedExcludes"
              :excludes="visibleExcludes"
              :show-limit="SHOW_LIMIT"
            >
              <template #show-more-button>
                <UButton
                  v-if="hasMoreExcludes"
                  variant="ghost"
                  color="white"
                  size="sm"
                  icon="heroicons:chevron-down-20-solid"
                  trailing
                  class="mt-2 w-full hover:!bg-transparent"
                  @click="showAllExcludes = true"
                >
                  Показать ещё
                </UButton>
              </template>
            </ProductExcludes>
          </div>

          <!-- Секция «Собрать набор» -->
          <ProductBundlesSection
            v-if="hasBundles"
            :bundles="bundles"
            :base-product-img="baseProductImgForBundle"
            class="more-about-dish__bundles"
            @select="openBundleBuilder"
          />

          <!-- Bottom Sheet: кастомизация обычного товара -->
          <ProductSettingsSheet
            v-model="showSettings"
            :current-variant="currentVariant"
            :selected-addons="selectedAddons"
            :selected-excludes="selectedExcludes"
            :visible-addons="visibleAddons"
            :visible-excludes="visibleExcludes"
            :has-more-addons="hasMoreAddons"
            :has-more-excludes="hasMoreExcludes"
            @update:selected-addons="selectedAddons = $event"
            @update:selected-excludes="selectedExcludes = $event"
            @show-more-addons="showAllAddons = true"
            @show-more-excludes="showAllExcludes = true"
          />

        </div>

        <!-- Действия — всегда прижаты к низу -->
        <ProductActions
          class="more-about-dish__actions"
          :in-cart="productState.inCart"
          :quantity="productState.quantity"
          :total-price="productState.totalPrice"
          :is-loading="isLoading"
          :is-disabled="isDisabled"
          :disabled-message="actionDisabledMessage"
          :step="quantityStep"
          :min-quantity="minQuantity"
          @add-to-cart="handlePrimaryPriceClick"
          @quantity-change="onQuantityChange"
          @open-basket="openBasket"
        />
        </div>
      </Transition>
    </div>

    <!-- Модалка с информацией о продукте -->
    <ProductInfoModal
      v-model="showProductInfo"
      :name="currentVariant.name"
      :description="currentVariant.description"
      :amount="currentVariant.amount"
      :volume="currentVariant.volume"
      :weight="currentVariant.weight"
      :kbju="currentVariant.kbju"
    />

    <!-- Экран «Собрать набор»: поверх медиа на десктопе, на всю модалку на мобильном -->
    <BundleBuilderOverlay
      v-if="selectedBundle"
      v-model="showBundleBuilder"
      :bundle="selectedBundle"
      :base-product-id="Number(currentVariant?.id)"
      :base-product-name="currentVariant.name"
      :base-product-img="baseProductImgForBundle"
      :placeholder-img="placeholderImg"
      :branch-id="branchId"
      :base-addons="addonsArr"
      :base-excludes="excludesArr"
      :base-addons-sum="addonsSum"
    />
  </div>
</template>

<style scoped lang="scss">
.more-about-dish {
  // relative — точка отсчёта для экрана «Собрать набор»: он ложится поверх модалки
  // absolute inset-0, как экраны выбора у комбо
  @apply relative h-full min-w-0;

  &__grid {
    @apply dark:bg-zinc-900;
    background-color: #1a1a1a;
    // Колонка с фото уже на 1024–1279: при 662px правой панели оставалось ~330px,
    // и в неё не помещались ни таб «Стандартная», ни строки добавок. С 1280px
    // ширины хватает обеим, и колонка возвращается к прежнему размеру.
    @apply grid lg:grid-cols-[571px_auto] xl:grid-cols-[662px_auto] gap-2;
    @apply max-lg:h-[calc(100dvh-var(--safe-area-inset-top))];
    // Потолок высоты на десктопе: без него модалку тянул вниз список добавок, и на
    // невысоком окне (ноутбук 1024×640) кнопка «В корзину» оказывалась за экраном.
    // 4rem — вертикальные поля UModal (sm:my-8). Внутри уже есть свой скролл
    // (__content-scroll), так что содержимое не теряется.
    @apply lg:max-h-[calc(100dvh-4rem)];
    // Ряд `minmax(0, 1fr)` — не украшение: с одним лишь max-height дорожка остаётся
    // «по содержимому» и просто обрезается, то есть кнопка «В корзину» уходит под
    // нижнюю кромку. С 1fr высота ряда становится определённой, и внутренний скролл
    // правой колонки (__content-scroll) наконец включается.
    @apply lg:grid-rows-[minmax(0,1fr)];
    @apply max-lg:grid-rows-[auto_auto_1fr];
    @apply min-w-0 overflow-hidden;
    background-size: cover;
    background-position: center;
  }

  &__content {
    @apply text-start h-full min-h-0 pt-5 pb-4 px-5 lg:py-6 lg:px-6 xl:py-8 xl:px-8;
    @apply flex flex-col overflow-hidden min-w-0;
    position: relative;

    // Frosted glass: blur на самом flex-контейнере ломает расчёт высоты (WebKit).
    // Вынесено на ::before — контент остаётся обычным flex-столбцом.
    &::before {
      content: '';
      position: absolute;
      inset: 0;
      z-index: 0;
      pointer-events: none;
      border-radius: inherit;
      background-color: rgba(255, 255, 255, 0.55);
      backdrop-filter: blur(32px) saturate(1.6) brightness(1.05);
      -webkit-backdrop-filter: blur(32px) saturate(1.6) brightness(1.05);
    }

    .dark &::before {
      background-color: rgba(18, 18, 20, 0.68);
      backdrop-filter: blur(32px) saturate(1.4) brightness(0.9);
      -webkit-backdrop-filter: blur(32px) saturate(1.4) brightness(0.9);
    }

    & > * {
      position: relative;
      z-index: 1;
    }

    // Всегда в 3-й строке сетки на мобильных.
    // Строка 2 (customize-button) рендерится условно через v-if:
    // без явного grid-row content попадает в строку 2 и теряет высоту 1fr.
    @media (max-width: 1023px) {
      grid-row: 3;
      border-top-left-radius: 1rem;
      border-top-right-radius: 1rem;
    }
  }

  &__content-scroll {
    @apply flex-1 min-h-0 overflow-y-auto overflow-x-hidden;

    scrollbar-width: thin;
    scrollbar-color: rgba(156, 163, 175, 0.4) transparent;

    &::-webkit-scrollbar {
      width: 4px;
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    &::-webkit-scrollbar-thumb {
      background-color: rgba(156, 163, 175, 0.4);
      border-radius: 2px;
    }
  }

  &__title {
    @apply text-lg xl:text-xl font-bold;
    // Название блюда в карточке товара — одно из мест фирменного шрифта
    // (см. раздел «Внешний вид» и shared/config/appearance.ts)
    font-family: var(--app-brand-font, inherit);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: calc(100% - 3.75rem);
    width: 100%;
    height: 41px;
    min-height: fit-content;
  }

  &__header-actions {
    @apply flex gap-3 justify-between items-center;
  }

  &__info-button {
    @apply flex items-center justify-center gap-2 text-gray-900 dark:text-white;
    @apply hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors;
    @apply my-4 md:hidden;
  }

  &__disabled-message {
    @apply text-red-500 text-sm;
  }

  &__info--desktop {
    @apply hidden md:block;
  }

  &__settings--desktop {
    @apply hidden md:grid md:max-h-[300px] overflow-y-auto pl-2 pr-4 gap-4;
  }

  &__customize-button {
    @apply md:hidden ml-auto -mt-28 mr-5;
  }

  &__actions {
    @apply flex-shrink-0 pt-5;
  }

  &__bundles {
    @apply mb-3 mt-5 md:mt-10 min-w-0 max-w-full;
  }

}

// Vue <Transition name="more-about-dish-content" appear>: контент выезжает снизу при открытии модалки
.more-about-dish-content-enter-active {
  transition:
    transform 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.5s,
    opacity 0.5s ease-out 0.5s;
  will-change: transform, opacity;
}

.more-about-dish-content-enter-from {
  opacity: 0;
  transform: translate3d(0, 1rem, 0);
}

@media (max-width: 1023px) {
  .more-about-dish-content-enter-from {
    transform: translate3d(0, 2.75rem, 0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .more-about-dish-content-enter-active {
    transition: opacity 0.5s ease 0.5s;
    will-change: auto;
  }

  .more-about-dish-content-enter-from {
    transform: none;
  }
}

@keyframes shakeWithPause {
  0%,
  6%,
  100% {
    transform: rotate(0deg);
  }
  1%,
  3% {
    transform: rotate(-3deg);
  }
  2%,
  4% {
    transform: rotate(3deg);
  }
  5% {
    transform: rotate(-2deg);
  }
}

.shake-animation {
  position: relative;
  animation: shakeWithPause 8s ease-in-out infinite;

  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    background-color: rgba(249, 115, 22, 0.9);
    pointer-events: none;
  }

  &::before {
    width: 16px;
    height: 16px;
    bottom: -6px;
    right: 9px;
  }

  &::after {
    width: 8px;
    height: 8px;
    bottom: -14px;
    right: 17px;
  }
}
</style>
